// Board geometry for the detectors, over the bare `Board` of
// `@chess/api-mistakes` (pieces from a FEN, attacks, a move played from UCI):
// the rays a slider looks along and the first two pieces on each (a pin or
// a skewer), the king's square and its neighbours, whether a piece is safe
// where it stands (nobody attacks it, or it is defended and worth no more
// than the cheapest attacker), and the squares a piece could run to. Nothing
// here knows the rules beyond attacks; legality (a trapped piece's escapes,
// a mate) is asked of the `chess` service by the detector that needs it.
import type { Color, Fen, Square, Uci } from '@chess/api-model';
import { Board, opposite, VALUE, type Piece, type PieceKind } from '@chess/api-mistakes';

export { Board, opposite, VALUE };
export type { Piece, PieceKind };

export type Placed = { square: Square } & Piece;
/** A unit step on the board: file delta, rank delta. */
export type Step = readonly [number, number];

const FILES = 'abcdefgh';
export const fileOf = (sq: Square): number => FILES.indexOf(sq[0]);
export const rankOf = (sq: Square): number => Number(sq[1]) - 1;
export const squareAt = (file: number, rank: number): Square | undefined => (file >= 0 && file < 8 && rank >= 0 && rank < 8 ? `${FILES[file]}${rank + 1}` : undefined);

export const ROOK_STEPS: readonly Step[] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
export const BISHOP_STEPS: readonly Step[] = [[1, 1], [1, -1], [-1, 1], [-1, -1]];
export const KING_STEPS: readonly Step[] = [...ROOK_STEPS, ...BISHOP_STEPS];

export const isSlider = (kind: PieceKind): boolean => kind === 'bishop' || kind === 'rook' || kind === 'queen';

/** The steps a slider looks along. */
export function sliderSteps(kind: PieceKind): readonly Step[] {
  switch (kind) {
    case 'bishop':
      return BISHOP_STEPS;
    case 'rook':
      return ROOK_STEPS;
    case 'queen':
      return KING_STEPS;
    default:
      return [];
  }
}

/** The unit step from one square to another when they share a rank, file or diagonal. */
export function stepBetween(from: Square, to: Square): Step | undefined {
  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);
  if (df === 0 && dr === 0) return undefined;
  if (df !== 0 && dr !== 0 && Math.abs(df) !== Math.abs(dr)) return undefined;
  return [Math.sign(df), Math.sign(dr)];
}

/** Chebyshev distance: how many king moves apart. */
export function distance(a: Square, b: Square): number {
  return Math.max(Math.abs(fileOf(a) - fileOf(b)), Math.abs(rankOf(a) - rankOf(b)));
}

/** The squares from `from` along `step` to the edge, nearest first. */
export function ray(from: Square, step: Step): Square[] {
  const out: Square[] = [];
  for (let i = 1; ; i++) {
    const sq = squareAt(fileOf(from) + step[0] * i, rankOf(from) + step[1] * i);
    if (!sq) return out;
    out.push(sq);
  }
}

/** The first `count` pieces along a ray, nearest first, empty squares skipped. */
export function piecesAlong(board: Board, from: Square, step: Step, count = 2): Placed[] {
  const out: Placed[] = [];
  for (const sq of ray(from, step)) {
    const piece = board.get(sq);
    if (piece) {
      out.push({ square: sq, ...piece });
      if (out.length === count) break;
    }
  }
  return out;
}

export function kingSquare(board: Board, colour: Color): Square | undefined {
  return board.pieces().find((p) => p.kind === 'king' && p.colour === colour)?.square;
}

/** The squares around one, on the board. */
export function neighbours(sq: Square): Square[] {
  return KING_STEPS.map(([df, dr]) => squareAt(fileOf(sq) + df, rankOf(sq) + dr)).filter((s): s is Square => !!s);
}

/** The cheapest of `colour`'s pieces attacking a square, by value; undefined when none does. */
export function cheapestAttacker(board: Board, target: Square, colour: Color): Placed | undefined {
  return board
    .attackers(target, colour)
    .map((square) => ({ square, ...board.get(square)! }))
    .sort((a, b) => VALUE[a.kind] - VALUE[b.kind])[0];
}

export function defended(board: Board, square: Square, colour: Color): boolean {
  return board.attackers(square, colour).length > 0;
}

/**
 * Whether the piece on a square is safe there: nobody attacks it, or it is
 * defended and worth no more than the cheapest attacker (so taking it is a
 * fair trade at best). A king is safe when not attacked.
 */
export function safe(board: Board, square: Square): boolean {
  const piece = board.get(square);
  if (!piece) return true;
  const enemy = opposite(piece.colour);
  const attacker = cheapestAttacker(board, square, enemy);
  if (!attacker) return true;
  if (piece.kind === 'king') return false;
  return defended(board, square, piece.colour) && VALUE[piece.kind] <= VALUE[attacker.kind];
}

/** Whether taking the piece on `square` with `by` wins it: it is worth more than `by`, or nobody defends it. */
export function winnable(board: Board, square: Square, by: PieceKind): boolean {
  const piece = board.get(square);
  if (!piece) return false;
  if (piece.kind === 'king') return true;
  return VALUE[piece.kind] > VALUE[by] || !defended(board, square, piece.colour);
}

/** The piece a move puts on its target square, as it stands after the move (promoted when the move promotes). */
export function moved(board: Board, uci: Uci): { after: Board; from: Square; to: Square; piece: Placed; captured?: Placed } | undefined {
  const from = uci.slice(0, 2);
  const to = uci.slice(2, 4);
  if (!board.get(from)) return undefined;
  const captured = board.captured(uci);
  const after = board.play(uci);
  const piece = after.get(to);
  return piece ? { after, from, to, piece: { square: to, ...piece }, captured } : undefined;
}

/** The pieces a slider on `from` attacks first along each of its rays, with the piece straight behind each when there is one. */
export interface ThroughPair {
  front: Placed;
  behind?: Placed;
  step: Step;
}
export function throughPairs(board: Board, from: Square): ThroughPair[] {
  const piece = board.get(from);
  if (!piece || !isSlider(piece.kind)) return [];
  const out: ThroughPair[] = [];
  for (const step of sliderSteps(piece.kind)) {
    const [front, behind] = piecesAlong(board, from, step, 2);
    if (front) out.push({ front, behind, step });
  }
  return out;
}

/** `queen`, `rook`, `minor` or `pawn`: how the hanging and trapped variations name a piece. */
export function pieceClass(kind: PieceKind): 'queen' | 'rook' | 'minor' | 'pawn' | undefined {
  switch (kind) {
    case 'queen':
      return 'queen';
    case 'rook':
      return 'rook';
    case 'bishop':
    case 'knight':
      return 'minor';
    case 'pawn':
      return 'pawn';
    default:
      return undefined;
  }
}

/** The rank index (0–7) `colour`'s pawns promote on, and their home rank. */
export const promotionRank = (colour: Color): number => (colour === 'white' ? 7 : 0);
export const homeRank = (colour: Color): number => (colour === 'white' ? 0 : 7);
export const forward = (colour: Color): number => (colour === 'white' ? 1 : -1);

/** The FEN's side to move. */
export const sideToMove = (fen: Fen): Color => (fen.split(' ')[1] === 'b' ? 'black' : 'white');
